import React, { useRef } from 'react';
import { useDrag, useDrop, DropTargetMonitor } from 'react-dnd';

import { GqlCustomNavigationItem, CustomNavigationItemType } from 'types/graphql';
import { MENU_ITEM_DRAG_TYPE } from '../custom-navigation.constants';
import { NavigationItemContent } from './navigation-item-content';

type NavigationItemDragItem = {
  id: string;
  index: number;
  type: 'navigation-item';
};

type DraggableNavigationItemProps = {
  index: number;
  item: GqlCustomNavigationItem;
  onEdit: (itemId: string) => void;
  onMove: (dragIndex: number, hoverIndex: number) => void;
  onRemove: (itemId: string) => void;
  onSectionClick: (sectionId: string) => void;
};

export function DraggableNavigationItem({
  index,
  item,
  onEdit,
  onMove,
  onRemove,
  onSectionClick,
}: DraggableNavigationItemProps): JSX.Element {
  const ref = useRef<HTMLDivElement | null>(null);

  const [{ isDragging }, drag] = useDrag<NavigationItemDragItem, unknown, { isDragging: boolean }>({
    item: { type: MENU_ITEM_DRAG_TYPE, id: item.id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop<NavigationItemDragItem, unknown, unknown>({
    accept: MENU_ITEM_DRAG_TYPE,
    hover: (dragItem: NavigationItemDragItem, monitor: DropTargetMonitor) => {
      if (!ref.current) {
        return;
      }

      if (dragItem.id === item.id) {
        return;
      }

      const dragIndex = dragItem.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();

      if (!clientOffset) {
        return;
      }

      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      onMove(dragIndex, hoverIndex);
      dragItem.index = hoverIndex;
    },
  });

  drag(drop(ref));

  const isSection = item.navigationType === CustomNavigationItemType.section;

  const handleItemClick = (): void => {
    if (isSection) {
      onSectionClick(item.id);
    }
  };

  return (
    <NavigationItemContent
      isDragging={isDragging}
      isSection={isSection}
      item={item}
      onEdit={() => onEdit(item.id)}
      onItemClick={handleItemClick}
      onRemove={() => onRemove(item.id)}
      ref={ref}
    />
  );
}
